import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Line, Bar } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
} from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

const MetaAdsAnalytics = () => {
  const { token: authToken, userLanguage } = useSelector((state) => state.auth);
  const t = {
    title: { en: "Meta Ads Analytics", fr: "Analytique des publicites Meta", es: "Analitica de anuncios Meta", zh: "Meta广告分析", pt: "Analise de anuncios Meta", ar: "تحليلات إعلانات ميتا" },
    refresh: { en: "Refresh", fr: "Actualiser", es: "Actualizar", zh: "刷新", pt: "Atualizar", ar: "تحديث" },
    refreshing: { en: "Refreshing...", fr: "Actualisation...", es: "Actualizando...", zh: "刷新中...", pt: "Atualizando...", ar: "جارٍ التحديث..." },
    adAccount: { en: "Ad account", fr: "Compte publicitaire", es: "Cuenta publicitaria", zh: "广告账户", pt: "Conta de anuncios", ar: "حساب الإعلانات" },
    period: { en: "Period", fr: "Periode", es: "Periodo", zh: "周期", pt: "Periodo", ar: "الفترة" },
    l7: { en: "Last 7 days", fr: "7 derniers jours", es: "Ultimos 7 dias", zh: "最近7天", pt: "Ultimos 7 dias", ar: "آخر 7 أيام" },
    l14: { en: "Last 14 days", fr: "14 derniers jours", es: "Ultimos 14 dias", zh: "最近14天", pt: "Ultimos 14 dias", ar: "آخر 14 يومًا" },
    l30: { en: "Last 30 days", fr: "30 derniers jours", es: "Ultimos 30 dias", zh: "最近30天", pt: "Ultimos 30 dias", ar: "آخر 30 يومًا" },
    impressions: { en: "Impressions", fr: "Impressions", es: "Impresiones", zh: "展示", pt: "Impressoes", ar: "مرات الظهور" },
    clicks: { en: "Clicks", fr: "Clics", es: "Clics", zh: "点击", pt: "Cliques", ar: "النقرات" },
    ctr: { en: "CTR", fr: "CTR", es: "CTR", zh: "点击率", pt: "CTR", ar: "معدل النقر" },
    spend: { en: "Spend", fr: "Depenses", es: "Gasto", zh: "花费", pt: "Gasto", ar: "الإنفاق" },
    results: { en: "Results", fr: "Resultats", es: "Resultados", zh: "结果", pt: "Resultados", ar: "النتائج" },
    spendOverTime: { en: "Spend over time", fr: "Depenses dans le temps", es: "Gasto en el tiempo", zh: "花费趋势", pt: "Gasto ao longo do tempo", ar: "الإنفاق عبر الزمن" },
    clicksVsImpressions: { en: "Clicks vs Impressions", fr: "Clics vs Impressions", es: "Clics vs Impresiones", zh: "点击与展示", pt: "Cliques vs Impressoes", ar: "النقرات مقابل الظهور" },
    dailyPerformance: { en: "Daily performance", fr: "Performance quotidienne", es: "Rendimiento diario", zh: "每日表现", pt: "Desempenho diario", ar: "الأداء اليومي" },
    date: { en: "Date", fr: "Date", es: "Fecha", zh: "日期", pt: "Data", ar: "التاريخ" },
    reach: { en: "Reach", fr: "Portee", es: "Alcance", zh: "覆盖人数", pt: "Alcance", ar: "الوصول" },
    noData: { en: "No data.", fr: "Aucune donnee.", es: "Sin datos.", zh: "暂无数据。", pt: "Sem dados.", ar: "لا توجد بيانات." },
    failLoad: { en: "Failed to load insights", fr: "Echec du chargement des statistiques", es: "Error al cargar estadisticas", zh: "加载数据失败", pt: "Falha ao carregar dados", ar: "فشل تحميل الإحصاءات" },
  };
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccountIdx, setSelectedAccountIdx] = useState(0);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${authToken}` });
  const selectedAccount = adAccounts[selectedAccountIdx];

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: headers() })
      .then((res) => setAdAccounts(res.data.data || []))
      .catch(() => setAdAccounts([]));
  }, [authToken]);

  const loadInsights = () => {
    if (!selectedAccount?.accountId) return;
    setLoading(true);
    axios
      .get(`/meta/insights/account/${encodeURIComponent(selectedAccount.accountId)}?date_preset=${encodeURIComponent(datePreset)}`, {
        headers: headers(),
      })
      .then((res) => setRows(res.data.data || []))
      .catch((e) => toast.error(e.response?.data?.message || t.failLoad[userLanguage] || t.failLoad.en))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!selectedAccount?.accountId) return;
    loadInsights();
  }, [selectedAccount?.accountId, datePreset]);

  const totals = useMemo(() => {
    const t = {
      impressions: 0,
      reach: 0,
      clicks: 0,
      spend: 0,
      results: 0,
    };
    for (const r of rows) {
      t.impressions += num(r.impressions);
      t.reach += num(r.reach);
      t.clicks += num(r.clicks);
      t.spend += num(r.spend);
      const actions = Array.isArray(r.actions) ? r.actions : [];
      const actionPriority = ["purchase", "offsite_conversion.purchase", "lead", "add_to_cart", "link_click"];
      let rowResult = 0;
      for (const type of actionPriority) {
        const hit = actions.find((a) => a.action_type === type);
        if (hit) {
          rowResult = num(hit.value);
          break;
        }
      }
      if (!rowResult && actions.length > 0) {
        rowResult = num(actions[0].value);
      }
      t.results += rowResult;
    }
    const ctr = t.impressions ? (t.clicks / t.impressions) * 100 : 0;
    const cpc = t.clicks ? t.spend / t.clicks : 0;
    return { ...t, ctr, cpc };
  }, [rows]);

  const chartData = useMemo(() => {
    const labels = rows.map((r) => r.date_start || r.date_stop || "");
    const spend = rows.map((r) => num(r.spend));
    const clicks = rows.map((r) => num(r.clicks));
    const impressions = rows.map((r) => num(r.impressions));
    return { labels, spend, clicks, impressions };
  }, [rows]);

  const spendLine = {
    labels: chartData.labels,
    datasets: [
      {
        label: `${t.spend[userLanguage] || t.spend.en} (USD)`,
        data: chartData.spend,
        borderColor: "#20a8d8",
        backgroundColor: "rgba(32,168,216,0.1)",
        fill: true,
      },
    ],
  };

  const clicksImpressionsBar = {
    labels: chartData.labels,
    datasets: [
      {
        label: t.clicks[userLanguage] || t.clicks.en,
        data: chartData.clicks,
        backgroundColor: "rgba(77,189,116,0.6)",
      },
      {
        label: t.impressions[userLanguage] || t.impressions.en,
        data: chartData.impressions,
        backgroundColor: "rgba(32,168,216,0.4)",
      },
    ],
  };

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>{t.title[userLanguage] || t.title.en}</b>
              <Button color="secondary" size="sm" onClick={loadInsights} disabled={loading || !selectedAccount?.accountId}>
                {loading ? t.refreshing[userLanguage] || t.refreshing.en : t.refresh[userLanguage] || t.refresh.en}
              </Button>
            </CardHeader>
            <CardBody>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>{t.adAccount[userLanguage] || t.adAccount.en}</Label>
                    <Input
                      type="select"
                      value={String(selectedAccountIdx)}
                      onChange={(e) => setSelectedAccountIdx(Number(e.target.value))}
                    >
                      {adAccounts.map((a, idx) => (
                        <option key={a.id} value={idx}>
                          {a.accountName || a.accountId}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>{t.period[userLanguage] || t.period.en}</Label>
                    <Input type="select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                      <option value="last_7d">{t.l7[userLanguage] || t.l7.en}</option>
                      <option value="last_14d">{t.l14[userLanguage] || t.l14.en}</option>
                      <option value="last_30d">{t.l30[userLanguage] || t.l30.en}</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={2}>
                  <Card body>
                    <b>{t.impressions[userLanguage] || t.impressions.en}</b>
                    <div>{totals.impressions.toLocaleString()}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>{t.clicks[userLanguage] || t.clicks.en}</b>
                    <div>{totals.clicks.toLocaleString()}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>{t.ctr[userLanguage] || t.ctr.en}</b>
                    <div>{totals.ctr.toFixed(2)}%</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>{t.spend[userLanguage] || t.spend.en}</b>
                    <div>${totals.spend.toFixed(2)}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>{t.results[userLanguage] || t.results.en}</b>
                    <div>{totals.results.toLocaleString()}</div>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Card>
                    <CardHeader><b>{t.spendOverTime[userLanguage] || t.spendOverTime.en}</b></CardHeader>
                    <CardBody>
                      <Line data={spendLine} />
                    </CardBody>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <CardHeader><b>{t.clicksVsImpressions[userLanguage] || t.clicksVsImpressions.en}</b></CardHeader>
                    <CardBody>
                      <Bar data={clicksImpressionsBar} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className="mt-3">
                <CardHeader><b>{t.dailyPerformance[userLanguage] || t.dailyPerformance.en}</b></CardHeader>
                <CardBody>
                  <Table bordered striped responsive size="sm">
                    <thead>
                      <tr>
                        <th>{t.date[userLanguage] || t.date.en}</th>
                        <th>{t.impressions[userLanguage] || t.impressions.en}</th>
                        <th>{t.reach[userLanguage] || t.reach.en}</th>
                        <th>{t.clicks[userLanguage] || t.clicks.en}</th>
                        <th>{t.spend[userLanguage] || t.spend.en}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="py-4 text-center">
                            <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-4 text-center text-muted">
                            {t.noData[userLanguage] || t.noData.en}
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.date_start || "-"}</td>
                            <td>{num(r.impressions).toLocaleString()}</td>
                            <td>{num(r.reach).toLocaleString()}</td>
                            <td>{num(r.clicks).toLocaleString()}</td>
                            <td>${num(r.spend).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaAdsAnalytics;
